import { Fragment, useState } from "react";
import { createFileRoute, useRouter } from "@tanstack/react-router";
import { usePageView } from "@/hooks/usePageView";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  useAdminUsageSummary,
  useAdminUsageByUser,
  useAdminUsageTimeline,
  useAdminUsageByFeature,
  useAdminActivity,
  useAdminTesterRollup,
} from "@/hooks/useAdminUsage";
import {
  useAdminPerformance,
  useAdminPdfStats,
  useAdminEngagement,
} from "@/hooks/useAdminInstrumentation";
import { useAdminFunnel, useAdminFeatureAdoption } from "@/hooks/useAdminFunnel";
import { FeedbackTab } from "@/components/admin/FeedbackTab";
import { ReconciliationTab } from "@/components/admin/ReconciliationTab";
import { SentryTab } from "@/components/admin/SentryTab";
import { useAdminProfitSummary, useAdminProfitByUser } from "@/hooks/useAdminProfit";
import { useAdminSystem } from "@/hooks/useAdminSystem";

const PERIOD_OPTIONS = [
  { label: "7 days", value: 7 },
  { label: "30 days", value: 30 },
  { label: "90 days", value: 90 },
] as const;

const tabs = [
  { id: "costs", label: "Costs" },
  { id: "activity", label: "Activity" },
  { id: "by-feature", label: "By Feature" },
  { id: "performance", label: "Performance" },
  { id: "pdf", label: "PDF Retrieval" },
  { id: "engagement", label: "Engagement" },
  { id: "adoption", label: "Feature Adoption" },
  { id: "feedback", label: "Feedback" },
  { id: "sentry", label: "Sentry" },
  { id: "funnel", label: "Funnel" },
  { id: "profit", label: "Profit" },
  { id: "reconciliation", label: "Reconciliation" },
  { id: "system", label: "System" },
] as const;

type TabId = (typeof tabs)[number]["id"];

const validTabs = new Set<string>(tabs.map((t) => t.id));

export const Route = createFileRoute("/admin/usage")({
  validateSearch: (search: Record<string, unknown>): { tab: TabId } => ({
    tab: validTabs.has(search.tab as string) ? (search.tab as TabId) : "costs",
  }),
  component: AdminUsageDashboard,
});

function formatNumber(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString();
}

function formatCost(n: number): string {
  return `$${n.toFixed(2)}`;
}

function AdminUsageDashboard() {
  usePageView("/admin/usage");
  const router = useRouter();
  const { tab } = Route.useSearch();
  const navigate = Route.useNavigate();
  const [days, setDays] = useState(30);
  const [expandedFeatures, setExpandedFeatures] = useState<Set<string>>(new Set());

  const toggleFeature = (feature: string) =>
    setExpandedFeatures((prev) => {
      const next = new Set(prev);
      if (next.has(feature)) next.delete(feature);
      else next.add(feature);
      return next;
    });

  // All hooks must be called before any conditional returns
  const { data: summary, isLoading: summaryLoading } = useAdminUsageSummary(days);
  const { data: byUser } = useAdminUsageByUser(days);
  const { data: timeline } = useAdminUsageTimeline(days);
  const { data: perfData, isLoading: perfLoading } = useAdminPerformance(days);
  const { data: pdfData, isLoading: pdfLoading } = useAdminPdfStats(days);
  const { data: engData, isLoading: engLoading } = useAdminEngagement(days);
  const { data: featureData, isLoading: featureLoading } = useAdminUsageByFeature(days);
  const { data: activityData, isLoading: activityLoading } = useAdminActivity(days);
  const { data: rollupData, isLoading: rollupLoading } = useAdminTesterRollup(days);
  const { data: adoptionData, isLoading: adoptionLoading } = useAdminFeatureAdoption(days);
  const { data: funnelData, isLoading: funnelLoading } = useAdminFunnel(days);
  const { data: profitSummary, isLoading: profitLoading } = useAdminProfitSummary(days);
  const { data: profitByUser } = useAdminProfitByUser(days);
  const { data: systemData, isLoading: systemLoading } = useAdminSystem();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={() => router.history.back()}
            className="text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            &larr; Back
          </button>
          <span className="text-muted-foreground">/</span>
          <h1 className="text-2xl font-semibold">Usage</h1>
        </div>
        <select
          value={days}
          onChange={(e) => setDays(Number(e.target.value))}
          className="rounded-md border bg-background px-3 py-1.5 text-sm"
        >
          {PERIOD_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      {/* Tab Bar */}
      <div className="flex flex-wrap gap-1 border-b border-zinc-700 mb-6 overflow-x-auto">
        {tabs.map((t) => (
          <button
            key={t.id}
            onClick={() => navigate({ search: { tab: t.id } })}
            className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors whitespace-nowrap ${
              tab === t.id
                ? "bg-zinc-800 text-white border-b-2 border-blue-500"
                : "text-zinc-400 hover:text-zinc-200"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Costs Tab */}
      {tab === "costs" && (
        <>
          {/* Stat Cards */}
          <div className="grid grid-cols-4 gap-4">
            <StatCard
              title="Total Requests"
              value={summary ? formatNumber(summary.totals.requests) : "—"}
              loading={summaryLoading}
            />
            <StatCard
              title="Total Tokens"
              value={
                summary
                  ? formatNumber(summary.totals.input_tokens + summary.totals.output_tokens)
                  : "—"
              }
              subtitle={
                summary
                  ? `${formatNumber(summary.totals.input_tokens)} in / ${formatNumber(summary.totals.output_tokens)} out`
                  : undefined
              }
              loading={summaryLoading}
            />
            <StatCard
              title="Estimated Cost"
              value={summary ? formatCost(summary.totals.estimated_cost_usd) : "—"}
              loading={summaryLoading}
            />
            <StatCard
              title="Active Users"
              value={summary ? String(summary.totals.active_users) : "—"}
              loading={summaryLoading}
            />
          </div>

          {/* Timeline Chart */}
          {timeline && timeline.series.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Usage Over Time</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={timeline.series}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                    <XAxis
                      dataKey="timestamp"
                      tickFormatter={(v: string) =>
                        new Date(v).toLocaleDateString(undefined, { month: "short", day: "numeric" })
                      }
                      className="text-xs"
                    />
                    <YAxis
                      yAxisId="tokens"
                      tickFormatter={(v: number) => formatNumber(v)}
                      className="text-xs"
                    />
                    <YAxis
                      yAxisId="cost"
                      orientation="right"
                      tickFormatter={(v: number) => `$${v.toFixed(2)}`}
                      className="text-xs"
                    />
                    <Tooltip
                      labelFormatter={(v: unknown) => new Date(v as string).toLocaleDateString()}
                      formatter={(value: unknown, name: unknown) => {
                        const numVal = value as number;
                        const strName = name as string;
                        return strName === "estimated_cost_usd"
                          ? [formatCost(numVal), "Cost"]
                          : [formatNumber(numVal), strName.replace("_", " ")];
                      }}
                    />
                    <Area
                      yAxisId="tokens"
                      type="monotone"
                      dataKey="input_tokens"
                      stackId="tokens"
                      stroke="hsl(var(--chart-1))"
                      fill="hsl(var(--chart-1))"
                      fillOpacity={0.3}
                      name="input_tokens"
                    />
                    <Area
                      yAxisId="tokens"
                      type="monotone"
                      dataKey="output_tokens"
                      stackId="tokens"
                      stroke="hsl(var(--chart-2))"
                      fill="hsl(var(--chart-2))"
                      fillOpacity={0.3}
                      name="output_tokens"
                    />
                    <Area
                      yAxisId="cost"
                      type="monotone"
                      dataKey="estimated_cost_usd"
                      stroke="hsl(var(--chart-5))"
                      fill="none"
                      strokeWidth={2}
                      name="estimated_cost_usd"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}

          {/* Per-User Table */}
          {byUser && byUser.users.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Usage by User</CardTitle>
              </CardHeader>
              <CardContent>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-muted-foreground">
                      <th className="pb-2 font-medium">User</th>
                      <th className="pb-2 font-medium text-right">Requests</th>
                      <th className="pb-2 font-medium text-right">Input Tokens</th>
                      <th className="pb-2 font-medium text-right">Output Tokens</th>
                      <th className="pb-2 font-medium text-right">Est. Cost</th>
                    </tr>
                  </thead>
                  <tbody>
                    {byUser.users.map((u) => (
                      <tr key={u.user_id} className="border-b last:border-0">
                        <td className="py-2">{u.name}</td>
                        <td className="py-2 text-right">{formatNumber(u.requests)}</td>
                        <td className="py-2 text-right">{formatNumber(u.input_tokens)}</td>
                        <td className="py-2 text-right">{formatNumber(u.output_tokens)}</td>
                        <td className="py-2 text-right font-medium">{formatCost(u.estimated_cost_usd)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          )}

          {/* Per-Endpoint Breakdown */}
          {summary && Object.keys(summary.providers).length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">By Provider &amp; Endpoint</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {Object.entries(summary.providers).map(([provider, data]) => (
                  <details key={provider} open>
                    <summary className="cursor-pointer font-medium capitalize">
                      {provider}{" "}
                      <span className="text-muted-foreground font-normal">
                        — {formatNumber(data.total_requests)} requests, {formatCost(data.estimated_cost_usd)}
                      </span>
                    </summary>
                    <table className="mt-2 w-full text-sm">
                      <thead>
                        <tr className="border-b text-left text-muted-foreground">
                          <th className="pb-1 font-medium">Endpoint</th>
                          <th className="pb-1 font-medium text-right">Requests</th>
                          <th className="pb-1 font-medium text-right">Input</th>
                          <th className="pb-1 font-medium text-right">Output</th>
                          <th className="pb-1 font-medium text-right">Cost</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.entries(data.by_endpoint).map(([ep, stats]) => (
                          <tr key={ep} className="border-b last:border-0">
                            <td className="py-1 font-mono text-xs">{ep}</td>
                            <td className="py-1 text-right">{formatNumber(stats.requests)}</td>
                            <td className="py-1 text-right">{formatNumber(stats.input_tokens)}</td>
                            <td className="py-1 text-right">{formatNumber(stats.output_tokens)}</td>
                            <td className="py-1 text-right font-medium">{formatCost(stats.estimated_cost_usd ?? 0)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </details>
                ))}
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Performance Tab */}
      {tab === "performance" && (
        <div>
          <h2 className="text-lg font-semibold text-zinc-200 mb-4">Operation Latency</h2>
          {perfLoading ? (
            <p className="text-zinc-400">Loading...</p>
          ) : perfData?.by_endpoint?.length ? (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-zinc-400 border-b border-zinc-700">
                  <th className="pb-2">Endpoint</th>
                  <th className="pb-2 text-right">Count</th>
                  <th className="pb-2 text-right">Avg (ms)</th>
                  <th className="pb-2 text-right">P95 (ms)</th>
                </tr>
              </thead>
              <tbody>
                {perfData.by_endpoint.map((e) => (
                  <tr key={e.endpoint} className="border-b border-zinc-800">
                    <td className="py-2 text-zinc-200">{e.endpoint}</td>
                    <td className="py-2 text-right text-zinc-300">{e.count}</td>
                    <td className="py-2 text-right text-zinc-300">{e.avg_ms.toLocaleString()}</td>
                    <td className="py-2 text-right text-zinc-300">{e.p95_ms.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="text-zinc-500">No performance data yet.</p>
          )}
        </div>
      )}

      {/* PDF Retrieval Tab */}
      {tab === "pdf" && (
        <div>
          <h2 className="text-lg font-semibold text-zinc-200 mb-4">PDF Retrieval</h2>
          {pdfLoading ? (
            <p className="text-zinc-400">Loading...</p>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-4 mb-6">
                <StatCard title="Total Attempts" value={String(pdfData?.total_attempts ?? 0)} />
                <StatCard
                  title="Success Rate"
                  value={`${((pdfData?.success_rate ?? 0) * 100).toFixed(1)}%`}
                />
              </div>
              {pdfData?.by_tier?.length ? (
                <table className="w-full text-sm mb-6">
                  <thead>
                    <tr className="text-left text-zinc-400 border-b border-zinc-700">
                      <th className="pb-2">Tier</th>
                      <th className="pb-2 text-right">Attempts</th>
                      <th className="pb-2 text-right">Successes</th>
                      <th className="pb-2 text-right">Rate</th>
                      <th className="pb-2 text-right">Avg (ms)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pdfData.by_tier.map((t) => {
                      const rate = t.attempts > 0 ? t.successes / t.attempts : 0;
                      // Terminal failure sources set by the orchestrator when
                      // no tier returned content. These are 0%-success rows by
                      // construction — dim them so real-source performance is
                      // easier to read at a glance.
                      const isFailureTerminal = [
                        "unknown",
                        "disposition_blocked",
                        "exhausted_paywall",
                        "exhausted",
                        "budget_timeout",
                      ].includes(t.tier);
                      return (
                        <tr key={t.tier} className="border-b border-zinc-800">
                          <td className="py-2 text-zinc-200">
                            {t.tier}
                            {isFailureTerminal && (
                              <span className="ml-2 text-[10px] uppercase tracking-wide text-zinc-500">
                                failure
                              </span>
                            )}
                          </td>
                          <td className="py-2 text-right text-zinc-300">{t.attempts}</td>
                          <td className="py-2 text-right text-zinc-300">{t.successes}</td>
                          <td
                            className={
                              "py-2 text-right " +
                              (isFailureTerminal
                                ? "text-zinc-500"
                                : rate >= 0.9
                                  ? "text-emerald-400"
                                  : rate >= 0.5
                                    ? "text-yellow-400"
                                    : "text-red-400")
                            }
                          >
                            {(rate * 100).toFixed(0)}%
                          </td>
                          <td className="py-2 text-right text-zinc-300">
                            {t.avg_ms.toLocaleString()}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              ) : null}
              {pdfData?.common_errors?.length ? (
                <>
                  <h3 className="text-sm font-medium text-zinc-400 mb-2">Common Errors</h3>
                  <ul className="text-sm text-zinc-300">
                    {pdfData.common_errors.map((e, i) => (
                      <li key={i} className="mb-1">
                        {e.error}: {e.count}
                      </li>
                    ))}
                  </ul>
                </>
              ) : null}
            </>
          )}
        </div>
      )}

      {/* Engagement Tab */}
      {tab === "engagement" && (
        <div>
          <h2 className="text-lg font-semibold text-zinc-200 mb-4">Engagement</h2>
          {engLoading ? (
            <p className="text-zinc-400">Loading...</p>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-4 mb-6">
                <StatCard title="Active Users" value={String(engData?.active_users ?? 0)} />
                <StatCard title="Total Events" value={String(engData?.total_events ?? 0)} />
              </div>
              {engData?.feature_usage?.length ? (
                <>
                  <h3 className="text-sm font-medium text-zinc-400 mb-2">Feature Usage</h3>
                  <table className="w-full text-sm mb-6">
                    <thead>
                      <tr className="text-left text-zinc-400 border-b border-zinc-700">
                        <th className="pb-2">Feature</th>
                        <th className="pb-2 text-right">Count</th>
                      </tr>
                    </thead>
                    <tbody>
                      {engData.feature_usage.map((f) => (
                        <tr key={f.feature} className="border-b border-zinc-800">
                          <td className="py-2 text-zinc-200">{f.feature}</td>
                          <td className="py-2 text-right text-zinc-300">{f.count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              ) : null}
              {engData?.top_routes?.length ? (
                <>
                  <h3 className="text-sm font-medium text-zinc-400 mb-2">Top Routes</h3>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-zinc-400 border-b border-zinc-700">
                        <th className="pb-2">Route</th>
                        <th className="pb-2 text-right">Views</th>
                      </tr>
                    </thead>
                    <tbody>
                      {engData.top_routes.map((r) => (
                        <tr key={r.route} className="border-b border-zinc-800">
                          <td className="py-2 text-zinc-200">{r.route}</td>
                          <td className="py-2 text-right text-zinc-300">{r.views}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              ) : null}
            </>
          )}
        </div>
      )}

      {/* Activity Tab — both ledgers joined + per-tester rollup */}
      {tab === "activity" && (
        <div className="space-y-8">
          <div>
            <h2 className="text-lg font-semibold text-zinc-200 mb-1">Feature Activity</h2>
            <p className="text-xs text-zinc-500 mb-4">
              Runs from the quota ledger (tool_run_events) joined with requests/cost
              from the usage ledger (usage_events).
            </p>
            {activityLoading ? (
              <p className="text-zinc-400">Loading...</p>
            ) : activityData?.items?.length ? (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-zinc-400 border-b border-zinc-700">
                    <th className="pb-2">Feature</th>
                    <th className="pb-2 text-right">Runs</th>
                    <th className="pb-2 text-right">Requests</th>
                    <th className="pb-2 text-right">Input Tokens</th>
                    <th className="pb-2 text-right">Output Tokens</th>
                    <th className="pb-2 text-right">Users</th>
                    <th className="pb-2 text-right">Cost ($)</th>
                  </tr>
                </thead>
                <tbody>
                  {activityData.items.map((f) => {
                    const expandable = (f.endpoints?.length ?? 0) > 0;
                    const expanded = expandedFeatures.has(f.feature);
                    return (
                      <Fragment key={f.feature}>
                        <tr
                          className={`border-b border-zinc-800 ${expandable ? "cursor-pointer hover:bg-zinc-900/50" : ""}`}
                          onClick={expandable ? () => toggleFeature(f.feature) : undefined}
                          aria-expanded={expandable ? expanded : undefined}
                        >
                          <td className="py-2 text-zinc-200">
                            {expandable ? (
                              <span className="mr-1 inline-block w-3 text-zinc-500">
                                {expanded ? "▾" : "▸"}
                              </span>
                            ) : (
                              <span className="mr-1 inline-block w-3" />
                            )}
                            {f.feature}
                          </td>
                          <td className="py-2 text-right text-zinc-300">{formatNumber(f.runs)}</td>
                          <td className="py-2 text-right text-zinc-300">{formatNumber(f.requests)}</td>
                          <td className="py-2 text-right text-zinc-300">{formatNumber(f.input_tokens)}</td>
                          <td className="py-2 text-right text-zinc-300">{formatNumber(f.output_tokens)}</td>
                          <td className="py-2 text-right text-zinc-300">
                            {Math.max(f.run_users, f.usage_users)}
                          </td>
                          <td className="py-2 text-right font-medium text-zinc-300">{formatCost(f.estimated_cost_usd)}</td>
                        </tr>
                        {expanded &&
                          f.endpoints.map((ep) => (
                            <tr key={`${f.feature}:${ep.endpoint}`} className="border-b border-zinc-800/50 bg-zinc-900/30 text-xs">
                              <td className="py-1.5 pl-6 text-zinc-400">{ep.endpoint}</td>
                              <td className="py-1.5 text-right text-zinc-500">—</td>
                              <td className="py-1.5 text-right text-zinc-400">{formatNumber(ep.requests)}</td>
                              <td className="py-1.5 text-right text-zinc-400">{formatNumber(ep.input_tokens)}</td>
                              <td className="py-1.5 text-right text-zinc-400">{formatNumber(ep.output_tokens)}</td>
                              <td className="py-1.5 text-right text-zinc-500">—</td>
                              <td className="py-1.5 text-right text-zinc-400">{formatCost(ep.estimated_cost_usd)}</td>
                            </tr>
                          ))}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            ) : (
              <p className="text-zinc-500">No activity in this period.</p>
            )}
          </div>

          <div>
            <h2 className="text-lg font-semibold text-zinc-200 mb-1">Per-Tester Rollup</h2>
            <p className="text-xs text-zinc-500 mb-4">
              Daily spend per user; alert fires when today exceeds max($5, 3&times; the
              prior-week daily average).
            </p>
            {rollupLoading ? (
              <p className="text-zinc-400">Loading...</p>
            ) : rollupData?.users?.length ? (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-zinc-400 border-b border-zinc-700">
                    <th className="pb-2">User</th>
                    <th className="pb-2">Plan</th>
                    <th className="pb-2 text-right">Runs 7d</th>
                    <th className="pb-2 text-right">Today ($)</th>
                    <th className="pb-2 text-right">7d ($)</th>
                    <th className="pb-2 text-right">Period ($)</th>
                    <th className="pb-2 text-right">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {rollupData.users.map((u) => (
                    <tr key={u.user_id} className="border-b border-zinc-800">
                      <td className="py-2 text-zinc-200">
                        {u.email}
                        {u.tags.includes("beta") && (
                          <span className="ml-2 rounded bg-blue-900/60 px-1.5 py-0.5 text-[10px] text-blue-300">beta</span>
                        )}
                      </td>
                      <td className="py-2 text-zinc-400">{u.plan ?? "—"}</td>
                      <td className="py-2 text-right text-zinc-300">{u.runs_7d}</td>
                      <td className="py-2 text-right text-zinc-300">{formatCost(u.cost_today_usd)}</td>
                      <td className="py-2 text-right text-zinc-300">{formatCost(u.cost_7d_usd)}</td>
                      <td className="py-2 text-right font-medium text-zinc-300">{formatCost(u.cost_period_usd)}</td>
                      <td className="py-2 text-right">
                        {u.alert ? (
                          <span className="rounded bg-red-900/60 px-1.5 py-0.5 text-[10px] font-medium text-red-300">
                            ⚠ spend
                          </span>
                        ) : (
                          <span className="text-zinc-600 text-xs">ok</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="text-zinc-500">No user activity in this period.</p>
            )}
          </div>
        </div>
      )}

      {/* By Feature Tab */}
      {tab === "by-feature" && (
        <div>
          <h2 className="text-lg font-semibold text-zinc-200 mb-4">Usage by Feature</h2>
          {featureLoading ? (
            <p className="text-zinc-400">Loading...</p>
          ) : featureData?.features?.length ? (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-zinc-400 border-b border-zinc-700">
                  <th className="pb-2">Feature</th>
                  <th className="pb-2 text-right">Requests</th>
                  <th className="pb-2 text-right">Input Tokens</th>
                  <th className="pb-2 text-right">Output Tokens</th>
                  <th className="pb-2 text-right">Cost ($)</th>
                </tr>
              </thead>
              <tbody>
                {featureData.features.map((f) => (
                  <tr key={f.feature} className="border-b border-zinc-800">
                    <td className="py-2 text-zinc-200">{f.feature}</td>
                    <td className="py-2 text-right text-zinc-300">{formatNumber(f.requests)}</td>
                    <td className="py-2 text-right text-zinc-300">{formatNumber(f.input_tokens)}</td>
                    <td className="py-2 text-right text-zinc-300">{formatNumber(f.output_tokens)}</td>
                    <td className="py-2 text-right font-medium text-zinc-300">{formatCost(f.estimated_cost_usd)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="text-zinc-500">No feature usage data yet.</p>
          )}
        </div>
      )}

      {/* Feature Adoption Tab */}
      {tab === "adoption" && (
        <div>
          <h2 className="text-lg font-semibold text-zinc-200 mb-4">Feature Adoption</h2>
          {adoptionLoading ? (
            <p className="text-zinc-400">Loading...</p>
          ) : (
            <>
              {/* Feature stats table */}
              {adoptionData?.features?.length ? (
                <>
                  <h3 className="text-sm font-medium text-zinc-400 mb-2">Feature Statistics</h3>
                  <table className="w-full text-sm mb-6">
                    <thead>
                      <tr className="text-left text-zinc-400 border-b border-zinc-700">
                        <th className="pb-2">Feature</th>
                        <th className="pb-2 text-right">Unique Users</th>
                        <th className="pb-2 text-right">Total Uses</th>
                        <th className="pb-2 text-right">Repeat</th>
                        <th className="pb-2 text-right">One-and-Done</th>
                      </tr>
                    </thead>
                    <tbody>
                      {adoptionData.features.map((f) => (
                        <tr key={f.feature} className="border-b border-zinc-800">
                          <td className="py-2 text-zinc-200">{f.feature}</td>
                          <td className="py-2 text-right text-zinc-300">{f.unique_users}</td>
                          <td className="py-2 text-right text-zinc-300">{f.total_uses}</td>
                          <td className="py-2 text-right text-zinc-300">{f.repeat_users}</td>
                          <td className="py-2 text-right text-zinc-300">{f.one_and_done_users}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              ) : (
                <p className="text-zinc-500 mb-6">No feature adoption data yet.</p>
              )}

              {/* User matrix table */}
              {adoptionData?.user_matrix?.length && adoptionData?.features?.length ? (
                <>
                  <h3 className="text-sm font-medium text-zinc-400 mb-2">User Feature Matrix</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-zinc-400 border-b border-zinc-700">
                          <th className="pb-2">User</th>
                          {adoptionData.features.map((f) => (
                            <th key={f.feature} className="pb-2 text-right">{f.feature}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {adoptionData.user_matrix.map((u) => (
                          <tr key={u.user_id} className="border-b border-zinc-800">
                            <td className="py-2 text-zinc-200">{u.name}</td>
                            {adoptionData.features.map((f) => (
                              <td key={f.feature} className="py-2 text-right text-zinc-300">
                                {u.features[f.feature] ?? 0}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              ) : null}
            </>
          )}
        </div>
      )}

      {tab === "feedback" && <FeedbackTab days={days} />}

      {tab === "sentry" && <SentryTab />}

      {tab === "reconciliation" && <ReconciliationTab />}

      {/* Funnel Tab */}
      {tab === "funnel" && (
        <div>
          <h2 className="text-lg font-semibold text-zinc-200 mb-4">Conversion Funnel</h2>
          {funnelLoading ? (
            <p className="text-zinc-400">Loading...</p>
          ) : funnelData ? (
            <div className="space-y-3">
              {([
                { key: "total_users", label: "Total Users" },
                { key: "concept_created", label: "Concept Created" },
                { key: "seeds_added", label: "Seeds Added" },
                { key: "trawl_completed", label: "Trawl Completed" },
                { key: "scoring_completed", label: "Scoring Completed" },
                { key: "paper_reviewed", label: "Paper Reviewed" },
                { key: "analysis_run", label: "Analysis Run" },
                { key: "evidence_attached", label: "Evidence Attached" },
                { key: "writing_started", label: "Writing Started" },
              ] as const).map(({ key, label }) => {
                const count = funnelData[key];
                const pct = funnelData.total_users > 0
                  ? (count / funnelData.total_users) * 100
                  : 0;
                return (
                  <div key={key} className="flex items-center gap-4">
                    <span className="w-40 text-sm text-zinc-300 shrink-0">{label}</span>
                    <div className="flex-1 bg-zinc-800 rounded-full h-5 overflow-hidden">
                      <div
                        className="bg-blue-500 h-full rounded-full transition-all"
                        style={{ width: `${Math.max(pct, 1)}%` }}
                      />
                    </div>
                    <span className="w-20 text-right text-sm text-zinc-300">
                      {count} ({pct.toFixed(0)}%)
                    </span>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-zinc-500">No funnel data yet.</p>
          )}
        </div>
      )}

      {/* Profit Tab */}
      {tab === "profit" && (
        <div>
          <h2 className="text-lg font-semibold text-zinc-200 mb-4">Profit</h2>
          {profitLoading ? (
            <p className="text-zinc-400">Loading...</p>
          ) : (
            <>
              <div className="grid grid-cols-4 gap-4 mb-6">
                <StatCard
                  title="Revenue"
                  value={profitSummary ? formatCost(profitSummary.revenue_usd) : "--"}
                />
                <StatCard
                  title="Cost"
                  value={profitSummary ? formatCost(profitSummary.cost_usd) : "--"}
                />
                <StatCard
                  title="Gross Profit"
                  value={profitSummary ? formatCost(profitSummary.gross_profit_usd) : "--"}
                />
                <StatCard
                  title="Margin"
                  value={profitSummary ? `${profitSummary.margin_pct.toFixed(1)}%` : "--"}
                />
              </div>

              <div className="grid grid-cols-4 gap-4 mb-6">
                <StatCard
                  title="Paying Users"
                  value={profitSummary ? String(profitSummary.paying_users) : "--"}
                />
                <StatCard
                  title="Free Users"
                  value={profitSummary ? String(profitSummary.free_users) : "--"}
                />
                <StatCard
                  title="Avg Revenue/User"
                  value={profitSummary ? formatCost(profitSummary.avg_revenue_per_user) : "--"}
                />
                <StatCard
                  title="Avg Cost/User"
                  value={profitSummary ? formatCost(profitSummary.avg_cost_per_user) : "--"}
                />
              </div>

              {profitByUser?.users?.length ? (
                <>
                  <h3 className="text-sm font-medium text-zinc-400 mb-2">Per-User Profit</h3>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-zinc-400 border-b border-zinc-700">
                        <th className="pb-2">User</th>
                        <th className="pb-2">Plan</th>
                        <th className="pb-2 text-right">MRR</th>
                        <th className="pb-2 text-right">Cost</th>
                        <th className="pb-2 text-right">Gross Profit</th>
                        <th className="pb-2 text-right">Margin</th>
                      </tr>
                    </thead>
                    <tbody>
                      {profitByUser.users.map((u) => (
                        <tr key={u.user_id} className="border-b border-zinc-800">
                          <td className="py-2 text-zinc-200">{u.name}</td>
                          <td className="py-2 text-zinc-300">{u.plan}</td>
                          <td className="py-2 text-right text-zinc-300">{formatCost(u.mrr_usd)}</td>
                          <td className="py-2 text-right text-zinc-300">{formatCost(u.cost_usd)}</td>
                          <td className="py-2 text-right font-medium text-zinc-300">{formatCost(u.gross_profit_usd)}</td>
                          <td className="py-2 text-right text-zinc-300">{u.margin_pct.toFixed(1)}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              ) : null}
            </>
          )}
        </div>
      )}

      {/* System Tab */}
      {tab === "system" && (
        <div>
          <h2 className="text-lg font-semibold text-zinc-200 mb-4">System Status</h2>
          {systemLoading ? (
            <p className="text-zinc-400">Loading...</p>
          ) : systemData ? (
            <div className="space-y-6">
              {/* API Keys */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">API Keys</CardTitle>
                </CardHeader>
                <CardContent>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-zinc-400 border-b border-zinc-700">
                        <th className="pb-2">Key</th>
                        <th className="pb-2">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(systemData.keys).map(([key, val]) => (
                        <tr key={key} className="border-b border-zinc-800">
                          <td className="py-2 text-zinc-200 font-mono text-xs">{key}</td>
                          <td className="py-2 text-zinc-300">
                            {Array.isArray(val) ? val.join(", ") : val}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </CardContent>
              </Card>

              {/* Config & Models */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Config &amp; Models</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <h4 className="text-sm font-medium text-zinc-400 mb-2">Models</h4>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <span className="text-zinc-400">Scoring</span>
                      <span className="text-zinc-200 font-mono text-xs">{systemData.models.scoring_model}</span>
                      <span className="text-zinc-400">Chat</span>
                      <span className="text-zinc-200 font-mono text-xs">{systemData.models.chat_model}</span>
                    </div>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-zinc-400 mb-2">System</h4>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <span className="text-zinc-400">Python</span>
                      <span className="text-zinc-200">{systemData.system.python_version}</span>
                      <span className="text-zinc-400">App Version</span>
                      <span className="text-zinc-200">{systemData.system.app_version}</span>
                      <span className="text-zinc-400">Environment</span>
                      <span className="text-zinc-200">{systemData.system.environment}</span>
                      <span className="text-zinc-400">Uptime</span>
                      <span className="text-zinc-200">
                        {Math.floor(systemData.system.uptime_seconds / 3600)}h{" "}
                        {Math.floor((systemData.system.uptime_seconds % 3600) / 60)}m
                      </span>
                    </div>
                  </div>
                  {Object.keys(systemData.config).length > 0 && (
                    <div>
                      <h4 className="text-sm font-medium text-zinc-400 mb-2">Config</h4>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        {Object.entries(systemData.config).map(([k, v]) => (
                          <span key={k} className="contents">
                            <span className="text-zinc-400">{k}</span>
                            <span className="text-zinc-200 font-mono text-xs">{String(v)}</span>
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Database Metrics */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Database</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-4 gap-4">
                    <StatCard title="Total Size" value={`${systemData.database.total_size_mb.toFixed(1)} MB`} />
                    <StatCard title="Schema Version" value={systemData.database.schema_version} />
                    <StatCard title="Pool Size" value={String(systemData.database.pool.size)} />
                    <StatCard
                      title="Pool In/Out"
                      value={`${systemData.database.pool.checked_in}/${systemData.database.pool.checked_out}`}
                      subtitle={`overflow: ${systemData.database.pool.overflow}`}
                    />
                  </div>
                  {systemData.database.tables.length > 0 && (
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-zinc-400 border-b border-zinc-700">
                          <th className="pb-2">Table</th>
                          <th className="pb-2 text-right">Rows</th>
                          <th className="pb-2 text-right">Size (MB)</th>
                          <th className="pb-2 text-right">Index Hit %</th>
                        </tr>
                      </thead>
                      <tbody>
                        {systemData.database.tables.map((t) => (
                          <tr key={t.name} className="border-b border-zinc-800">
                            <td className="py-2 text-zinc-200 font-mono text-xs">{t.name}</td>
                            <td className="py-2 text-right text-zinc-300">{formatNumber(t.rows)}</td>
                            <td className="py-2 text-right text-zinc-300">{t.size_mb.toFixed(2)}</td>
                            <td className="py-2 text-right text-zinc-300">{t.index_hit_pct.toFixed(1)}%</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </CardContent>
              </Card>

              {/* Health Checks */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Health Checks</CardTitle>
                </CardHeader>
                <CardContent>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-zinc-400 border-b border-zinc-700">
                        <th className="pb-2">Service</th>
                        <th className="pb-2">Status</th>
                        <th className="pb-2">Detail</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(systemData.health).map(([svc, info]) => (
                        <tr key={svc} className="border-b border-zinc-800">
                          <td className="py-2 text-zinc-200">{svc}</td>
                          <td className="py-2">
                            <span
                              className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${
                                info.status === "ok" || info.status === "healthy"
                                  ? "bg-green-900/50 text-green-400"
                                  : "bg-red-900/50 text-red-400"
                              }`}
                            >
                              {info.status}
                            </span>
                          </td>
                          <td className="py-2 text-zinc-400 text-xs">{info.detail ?? "--"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </CardContent>
              </Card>
            </div>
          ) : (
            <p className="text-zinc-500">No system data available.</p>
          )}
        </div>
      )}
    </div>
  );
}

function StatCard({
  title,
  value,
  subtitle,
  loading,
}: {
  title: string;
  value: string;
  subtitle?: string;
  loading?: boolean;
}) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className={`text-2xl font-bold ${loading ? "animate-pulse" : ""}`}>{value}</div>
        {subtitle && <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>}
      </CardContent>
    </Card>
  );
}
